import {
  useAuthenticationStatus,
  useUserData,
  useSignInEmailPassword,
  useSignUpEmailPassword,
  useSignOut,
} from '@nhost/react';
import { useApolloClient } from '@apollo/client';
import { useCallback } from 'react';
import { setUser as setSentryUser } from '@/lib/sentry';
import { identify, resetAnalyticsUser } from '@/lib/posthog';

interface SignInResult {
  session: unknown | null;
  error: { error: string; message: string; status: number } | null;
}

interface SignUpResult {
  session: unknown | null;
  needsEmailVerification?: boolean;
  error: { error: string; message: string; status: number } | null;
}

export interface SignUpExtra {
  fullName: string;
  countryCode: 'BR' | 'PT' | 'FR' | 'ES' | 'OTHER';
  preferredLocale?: 'pt-BR' | 'pt-PT' | 'en' | 'es' | 'fr';
  marketingOptIn: boolean;
}

interface UseAuthReturn {
  user: ReturnType<typeof useUserData>;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isLoading: boolean;
  signingIn: boolean;
  signingUp: boolean;
  isError: boolean;
  error: { error: string; message: string; status: number } | null;
  signIn: (email: string, password: string) => Promise<SignInResult>;
  signUp: (email: string, password: string, extra: SignUpExtra) => Promise<SignUpResult>;
  signOut: () => Promise<{ error: { error: string; message: string; status: number } | null }>;
}

/** Nhost recusa signInEmailPassword quando ja existe sessao ativa no client. */
function isAlreadySignedIn(error: UseAuthReturn['error']): boolean {
  if (!error) return false;
  return error.error === 'already-signed-in' || /already signed in/i.test(error.message ?? '');
}

export function useAuth(): UseAuthReturn {
  const { isAuthenticated, isLoading } = useAuthenticationStatus();
  const user = useUserData();
  const signInResult = useSignInEmailPassword();
  const signUpResult = useSignUpEmailPassword();
  const { signOut: doSignOut } = useSignOut();
  const apollo = useApolloClient();

  // O papel vem de user.roles (auth.user_roles, definido no servidor).
  // Antes vinha de user.metadata.is_admin — e metadata e enviada pelo browser
  // no signUp, entao bastava cadastrar com { is_admin: true } pra abrir o painel.
  const isAdmin = Array.isArray(user?.roles) && user.roles.includes('admin');

  if (user) {
    setSentryUser({ id: user.id, email: user.email });
    identify(user.id, { email: user.email, locale: user.locale, is_admin: isAdmin });
  }

  const signIn: UseAuthReturn['signIn'] = useCallback(
    async (email, password) => {
      const attempt = async () =>
        (await signInResult.signInEmailPassword(email, password)) as unknown as SignInResult;

      const result = await attempt();
      // Uma sessao residual no client faz o Nhost recusar o login com
      // "User is already signed in", travando o formulario. Como o usuario
      // acabou de enviar credenciais, encerra a sessao antiga e tenta de novo.
      if (isAlreadySignedIn(result.error)) {
        await doSignOut();
        return attempt();
      }
      return result;
    },
    [signInResult, doSignOut],
  );

  const signUp: UseAuthReturn['signUp'] = useCallback(
    async (email, password, extra) => {
      // Nhost Auth aceita locale max 3 chars (ISO 639-1). pt-BR -> pt, en -> en.
      const baseLocale = ((extra.preferredLocale ?? 'pt').split('-')[0] ?? 'pt').slice(0, 3);
      return (await signUpResult.signUpEmailPassword(email, password, {
        displayName: extra.fullName,
        locale: baseLocale,
        metadata: {
          full_name: extra.fullName,
          country_code: extra.countryCode,
          preferred_locale: extra.preferredLocale ?? null,
          marketing_opt_in: extra.marketingOptIn,
        },
      })) as unknown as SignUpResult;
    },
    [signUpResult],
  );

  const signOut: UseAuthReturn['signOut'] = useCallback(async () => {
    resetAnalyticsUser();
    setSentryUser(null);
    const result = (await doSignOut()) as unknown as { error: UseAuthReturn['error'] };
    try { await apollo.clearStore(); } catch { /* ignore */ }
    return result;
  }, [doSignOut, apollo]);

  return {
    user,
    isAuthenticated,
    isAdmin,
    isLoading,
    signingIn: signInResult.isLoading,
    signingUp: signUpResult.isLoading,
    isError: signInResult.isError || signUpResult.isError,
    error: signInResult.error ?? signUpResult.error,
    signIn,
    signUp,
    signOut,
  };
}
